import { LogoutOutlined, MenuOutlined, UserOutlined } from '@ant-design/icons'
import { Button, Dropdown, Grid, Space } from 'antd'
import { EnvSwitcher } from '../EnvSwitcher'
import { useAuthStore } from '../../stores/authStore'
import { useLogo } from './useLogo'
import { useSignOut } from './useSignOut'

const { useBreakpoint } = Grid

interface AppHeaderProps {
  /** Mobile only — the sidebar lives in a drawer, which needs a trigger. */
  showMenuButton?: boolean
  onMenuClick?: () => void
}

export function AppHeader({ showMenuButton = false, onMenuClick }: AppHeaderProps) {
  const admin = useAuthStore((s) => s.admin)
  const screens = useBreakpoint()
  const logoUrl = useLogo()
  const handleLogout = useSignOut()

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: 8,
        // Header contents must be allowed to shrink, or a long admin email
        // pushes the bar wider than the viewport and the page scrolls sideways.
        minWidth: 0,
      }}
    >
      {/* On tablet and desktop the logo lives in the rail; phones have no rail,
          so the bar carries the menu button and the logo. */}
      {showMenuButton ? (
        <Space size="small" style={{ minWidth: 0 }}>
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={onMenuClick}
            aria-label="Open navigation menu"
          />
          <img src={logoUrl} alt="FursaHub" style={{ height: 26, display: 'block' }} />
        </Space>
      ) : (
        <span />
      )}

      <Space size={screens.sm ? 'middle' : 'small'} style={{ flexShrink: 0 }}>
        <EnvSwitcher />
        <Dropdown
          menu={{
            items: [
              // The email is dropped from the button below `md`, so surface it
              // here — otherwise there is no way to tell which account is signed in.
              ...(screens.md
                ? []
                : [
                    { key: 'account', label: admin?.email ?? 'Admin', disabled: true },
                    { key: 'account-divider', type: 'divider' as const },
                  ]),
              {
                key: 'logout',
                icon: <LogoutOutlined />,
                label: 'Logout',
                danger: true,
                onClick: handleLogout,
              },
            ],
          }}
          placement="bottomRight"
        >
          <Button icon={<UserOutlined />} type="text">
            {/* Emails are long and variable-length; rendering one on a phone
                squeezes everything else out of the bar. */}
            {screens.md ? (admin?.email ?? 'Admin') : null}
          </Button>
        </Dropdown>
      </Space>
    </div>
  )
}
